import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Star, MapPin, Phone, Mail, Clock, Utensils, Beer, Calendar, Users, ChevronUp, Menu, X, FileText, Bell } from "lucide-react";
import PreorderTemplate from "./pages/PreorderTemplate";
import AdminPage from "./pages/AdminPage";

// Hero background - user's uploaded pub image
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_d6203704-8e79-48ad-85bf-41bc73f4a771/artifacts/mbtm6c1d_image_2026-01-31_134447912.png";

// Establishment date - December 4th, 2009
const ESTABLISHMENT_DATE = { year: 2009, month: 12, day: 4 };

// Calculate years since establishment (updates on December 4th each year)
const getYearsSinceEstablishment = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
  const currentDay = now.getDate();
  
  let years = currentYear - ESTABLISHMENT_DATE.year;
  
  // If we haven't reached December 4th this year, subtract 1
  if (currentMonth < ESTABLISHMENT_DATE.month || 
      (currentMonth === ESTABLISHMENT_DATE.month && currentDay < ESTABLISHMENT_DATE.day)) {
    years -= 1;
  }
  
  return years;
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#menu", label: "Menu" },
    { href: "#drinks", label: "Drinks" },
    { href: "#live-sports", label: "Live Football" },
    { href: "#whats-on", label: "What's On" },
    { href: "#room-hire", label: "Room Hire" },
    { href: "#reviews", label: "Reviews" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? "bg-[#F5F1E6]/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="#" className={`font-playfair text-xl font-bold transition-colors duration-300 ${isScrolled ? "text-[#2C1810]" : "text-white"}`}>
            The Three Horseshoes
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`font-manrope text-sm font-medium transition-colors duration-300 hover:text-[#C17C3A] ${
                  isScrolled ? "text-[#2C1810]" : "text-white"
                }`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/preorder"
              data-testid="preorder-nav-btn"
              className="bg-[#C17C3A] text-white font-manrope text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#A0652D] transition-colors flex items-center gap-2"
            >
              <FileText size={16} />
              Preorder Form
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors duration-300 ${isScrolled ? "text-[#2C1810]" : "text-white"}`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <div className="flex flex-col gap-4 bg-[#F5F1E6]/95 backdrop-blur-md rounded-lg p-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-manrope text-[#2C1810] text-sm font-medium hover:text-[#C17C3A] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/preorder"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[#C17C3A] text-white font-manrope text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#A0652D] transition-colors text-center flex items-center justify-center gap-2"
              >
                <FileText size={16} />
                Preorder Form
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: `url(${HERO_IMAGE})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <p className="font-caveat text-[#C17C3A] text-2xl md:text-3xl mb-4 animate-fade-in-up opacity-0 animation-delay-100">
          Welcome to
        </p>
        <h1 className="font-playfair text-white text-5xl md:text-7xl font-bold tracking-tight mb-6 animate-fade-in-up opacity-0 animation-delay-200">
          The Three Horseshoes
        </h1>
        <p className="font-manrope text-white/90 text-lg md:text-xl mb-4 animate-fade-in-up opacity-0 animation-delay-300">
          Traditional Welsh Pub • Est. 2009
        </p>
        <div className="flex items-center justify-center gap-2 text-white/80 mb-8 animate-fade-in-up opacity-0 animation-delay-400">
          <MapPin size={18} />
          <span className="font-manrope text-sm">High St, Pentwyn-mawr, Newbridge, Newport NP11 4HN</span>
        </div>
        
        {/* Rating Badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-10 animate-fade-in-up opacity-0 animation-delay-500">
          <a 
            href="https://www.tripadvisor.co.uk/Restaurant_Review-g1471645-d6952102-Reviews-or45-The_Three_Horseshoes_Inn-Blackwood_Caerphilly_County_South_Wales_Wales.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tripadvisor-link"
            className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-colors duration-300"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className={i < 4 ? "fill-[#C17C3A] text-[#C17C3A]" : "fill-[#C17C3A]/40 text-[#C17C3A]/40"} />
              ))}
            </div>
            <span className="text-white font-manrope text-sm">4.4 on Tripadvisor</span>
          </a>
          <a 
            href="https://www.google.com/maps/place/The+Three+Horseshoes+Inn/data=!4m7!3m6!1s0x486e21cb3a93802b:0xf1f1d94ca49998b6!8m2!3d51.658722!4d-3.164292!16s%2Fg%2F11ckqrp9lg!19sChIJK4CTOsshbkgRtpiZpEzZ8fE?authuser=0&hl=en&rclk=1"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-maps-link"
            className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-colors duration-300"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className={i < 5 ? "fill-[#C17C3A] text-[#C17C3A]" : "fill-[#C17C3A]/40 text-[#C17C3A]/40"} />
              ))}
            </div>
            <span className="text-white font-manrope text-sm">4.6 on Google Maps</span>
          </a>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-6">
          <a
            href="#menu"
            data-testid="view-menu-btn"
            className="bg-[#C17C3A] text-white font-manrope font-semibold px-8 py-4 rounded-full hover:bg-[#A0652D] transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            View Our Menu
          </a>
          <Link
            to="/preorder"
            data-testid="preorder-btn"
            className="bg-transparent border-2 border-white text-white font-manrope font-semibold px-8 py-4 rounded-full hover:bg-white hover:text-[#2C1810] transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-2"
          >
            <FileText size={20} />
            Preorder Form
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// About Section - Now fetches from database
const AboutSection = () => {
  const [about, setAbout] = useState({
    title: "A Warm Welsh Welcome",
    subtitle: "",
    content: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/about`);
        const data = await res.json();
        if (data.title || data.content) {
          setAbout(data);
        }
      } catch (err) {
        // Use defaults on error - silent fail is acceptable here
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch about:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  if (loading) return null;

  return (
    <section id="about" data-testid="about-section" className="py-20 px-6 bg-[#F5F1E6]">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold mb-6">
          {about.title || "A Warm Welsh Welcome"}
        </h2>
        {about.subtitle && (
          <p className="font-caveat text-[#C17C3A] text-2xl mb-4">{about.subtitle}</p>
        )}
        <p className="font-manrope text-[#4A4A4A] text-lg leading-relaxed mb-8 whitespace-pre-line">
          {about.content || `Nestled in the heart of Pentwyn-mawr, The Three Horseshoes has been serving the local community 
          for over ${getYearsSinceEstablishment()} years. Step through our doors and experience the warmth of an open log fire, 
          the comfort of home-cooked food, and the hospitality that has made us a beloved local institution.`}
        </p>
        <div className="flex flex-wrap justify-center gap-8 mt-12">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#C17C3A]/10 rounded-full flex items-center justify-center">
              <Utensils className="text-[#C17C3A]" size={24} />
            </div>
            <span className="font-manrope text-[#2C1810] font-medium">Home Cooked Food</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#C17C3A]/10 rounded-full flex items-center justify-center">
              <Beer className="text-[#C17C3A]" size={24} />
            </div>
            <span className="font-manrope text-[#2C1810] font-medium">Traditional Cask Ales</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#C17C3A]/10 rounded-full flex items-center justify-center">
              <Users className="text-[#C17C3A]" size={24} />
            </div>
            <span className="font-manrope text-[#2C1810] font-medium">Family Friendly</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section - Hidden when empty
const GallerySection = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
        const data = await res.json();
        setImages(data.images || []);
      } catch (err) {
        // Keep empty on error - gallery will simply not show
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch gallery:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Don't render if no images
  if (loading || images.length === 0) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Take a Look</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold">Gallery</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="aspect-square overflow-hidden rounded-lg cursor-pointer group"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
                alt={image.original_name || "Gallery image"}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#C17C3A] transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${selectedImage.url}`}
            alt={selectedImage.original_name || "Gallery image"}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

// Updates Section - Hidden when empty or disabled
const UpdatesSection = () => {
  const [updates, setUpdates] = useState({
    title: "Latest Updates",
    content: "",
    show_section: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/updates`);
        const data = await res.json();
        setUpdates(data);
      } catch (err) {
        // Keep defaults
      } finally {
        setLoading(false);
      }
    };
    fetchUpdates();
  }, []);

  // Don't render if loading, no content, or show_section is false
  if (loading || !updates.content || !updates.show_section) return null;

  return (
    <section id="updates" data-testid="updates-section" className="py-16 px-6 bg-[#C17C3A]">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#C17C3A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Bell className="text-[#C17C3A]" size={24} />
            </div>
            <div>
              <h2 className="font-playfair text-[#2C1810] text-2xl font-semibold mb-3">
                {updates.title || "Latest Updates"}
              </h2>
              <p className="font-manrope text-[#4A4A4A] leading-relaxed whitespace-pre-line">
                {updates.content}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Menu Section - Now fetches from database
const MenuSection = () => {
  const [menuItems, setMenuItems] = useState({
    starters: [], mains: [], vegetarian: [], grills: [], 
    salads: [], sides: [], childrens: [], steakSauces: [], desserts: []
  });
  const [loading, setLoading] = useState(true);

  // Default menu items (used if database is empty)
  const defaultMenu = {
    starters: [
      { name: "Southern Fried Chicken Goujons", price: "£6.75", description: "Served with BBQ sauce" },
      { name: "Duck Liver & Orange Pate", price: "£5.75", description: "Served with warm brown toast" },
      { name: "Nachos", price: "£4.75", description: "With chilli topping £6.75" },
      { name: "Salmon & Dill Fishcake", price: "£6.75", description: "Served with sweet chilli dipping sauce" },
      { name: "Mozzarella Sticks", price: "£5.25", description: "Served with tomato salsa" },
      { name: "Prawn Cocktail", price: "£6.95", description: "Served with brown bread & butter" },
      { name: "Whitebait", price: "£6.75", description: "Served with a warm bread roll" },
      { name: "Homemade Soup of the Day", price: "£6.25", description: "Served with a warm bread roll" },
      { name: "Garlic Mushrooms", price: "£5.75", description: "" },
      { name: "Loaded Potato Skins", price: "£6.25", description: "Bacon & Cheese. Cheese & Onion £5.75" },
    ],
    mains: [
      { name: "Corned Beef Pie", price: "£11.50", description: "Served with real chips, peas & gravy" },
      { name: "Chicken Curry", price: "£11.50", description: "Served with real chips, rice or half & half" },
      { name: "Pork & Leek Sausage And Mash", price: "£10.50", description: "Served with onion gravy" },
      { name: "Homemade Chilli Con Carne", price: "£11.25", description: "Served with real chips, rice or half & half" },
      { name: "Southern Fried Chicken Burger", price: "£11.25", description: "Served with sweet potato fries" },
      { name: "Beer Battered Cod", price: "£12.75", description: "Served with real chips & peas" },
      { name: "Homemade Steak & Ale Pie", price: "£11.75", description: "With real chips" },
      { name: "Lasagne", price: "£12.75", description: "Served with garlic bread & side salad" },
    ],
    vegetarian: [
      { name: "Spinach & Ricotta Cannelloni (V)", price: "£11.50", description: "Served with garlic bread & side salad" },
      { name: "Arrabiata Pasta (V)", price: "£9.50", description: "Served with garlic bread" },
      { name: "Smokey Three Bean Chilli (VE)", price: "£11.50", description: "Served with real chips, rice or half & half" },
    ],
    grills: [
      { name: "8oz Bacon Steak", price: "£11.50", description: "Add egg or pineapple £0.50" },
      { name: "16oz Horseshoe Gammon", price: "£15.50", description: "Add egg or pineapple £0.50" },
      { name: "10oz Rump Steak", price: "£16.25", description: "" },
      { name: "10oz Sirloin Steak", price: "£20.50", description: "" },
      { name: "20oz Mixed Grill", price: "£21.50", description: "Sausage, lamb chop, pork chop, gammon, rump steak & fried egg" },
    ],
    salads: [
      { name: "Salmon & Prawn", price: "£14.75", description: "" },
      { name: "Chicken", price: "£11.25", description: "" },
      { name: "Cheddar Cheese", price: "£7.75", description: "" },
    ],
    sides: [
      { name: "Sweet Potato Fries", price: "£3.75", description: "" },
      { name: "Garlic Bread", price: "£3.75", description: "" },
      { name: "Real Chips", price: "£3.75", description: "" },
    ],
    childrens: [
      { name: "Fish Fingers", price: "£6.75", description: "With chips/mash & peas/beans" },
      { name: "Chicken Nuggets", price: "£6.75", description: "With chips/mash & peas/beans" },
      { name: "Sausage", price: "£6.75", description: "With chips/mash & peas/beans" },
    ],
    steakSauces: [
      { name: "Pepper", price: "£3.00", description: "" },
      { name: "Dianne", price: "£3.00", description: "" },
      { name: "Garlic", price: "£3.00", description: "" },
    ],
    desserts: [],
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu`);
        const data = await res.json();
        if (data.items && data.items.length > 0) {
          const grouped = {};
          Object.keys(defaultMenu).forEach(cat => grouped[cat] = []);
          data.items.forEach(item => {
            if (grouped[item.category]) {
              grouped[item.category].push(item);
            }
          });
          setMenuItems(grouped);
        } else {
          setMenuItems(defaultMenu);
        }
      } catch (err) {
        setMenuItems(defaultMenu);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const categories = [
    { key: "starters", label: "Starters", subtitle: "" },
    { key: "mains", label: "Main Courses", subtitle: "All served with Real Chips unless stated otherwise" },
    { key: "vegetarian", label: "Vegetarian & Vegan Dishes", subtitle: "All served with Real Chips unless stated otherwise" },
    { key: "grills", label: "Grills", subtitle: "All served with Real Chips, Tomato, Mushrooms, Onion Rings & Peas" },
  ];

  const smallCategories = [
    { key: "salads", label: "Salads" },
    { key: "sides", label: "Sides" },
    { key: "childrens", label: "Children's Menu" },
    { key: "steakSauces", label: "Steak Sauces" },
  ];

  if (loading) {
    return (
      <section id="menu" className="py-20 px-6 bg-white">
        <div className="flex justify-center items-center">
          <div className="w-8 h-8 border-4 border-[#C17C3A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="menu" data-testid="menu-section" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Taste the tradition</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold">Our Menu</h2>
        </div>

        {/* Main Categories */}
        {categories.map(cat => (
          menuItems[cat.key]?.length > 0 && (
            <div key={cat.key} className="mb-12">
              <h3 className="font-playfair text-[#2C1810] text-3xl font-semibold mb-2 text-center">{cat.label}</h3>
              {cat.subtitle && <p className="text-center font-manrope text-[#4A4A4A] text-sm mb-6">{cat.subtitle}</p>}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuItems[cat.key].map((item, index) => (
                  <div key={item.id || index} className="bg-[#F5F1E6] p-4 rounded-lg border border-[#E5E0D6]">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-manrope text-[#2C1810] font-medium">{item.name}</span>
                      <span className="font-manrope text-[#C17C3A] font-semibold ml-2">{item.price}</span>
                    </div>
                    {item.description && <p className="font-manrope text-[#4A4A4A] text-sm">{item.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {/* Small Categories Row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {smallCategories.map(cat => (
            menuItems[cat.key]?.length > 0 && (
              <div key={cat.key}>
                <h3 className="font-playfair text-[#2C1810] text-xl font-semibold mb-3 text-center">{cat.label}</h3>
                <div className="bg-[#F5F1E6] p-4 rounded-lg border border-[#E5E0D6]">
                  {menuItems[cat.key].map((item, index) => (
                    <div key={item.id || index} className="flex justify-between items-center py-2 border-b border-[#E5E0D6] last:border-0">
                      <span className="font-manrope text-[#2C1810] text-sm">{item.name}</span>
                      <span className="font-manrope text-[#C17C3A] font-semibold text-sm">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>

        {/* Desserts */}
        <div className="mb-8 max-w-2xl mx-auto">
          <h3 className="font-playfair text-[#2C1810] text-2xl font-semibold mb-4 text-center">Desserts</h3>
          <div className="bg-[#F5F1E6] p-6 rounded-lg border border-[#E5E0D6] text-center">
            <p className="font-manrope text-[#2C1810] mb-2">Selection of Desserts Available</p>
            <p className="font-manrope text-[#4A4A4A] text-sm mb-2">Please ask for today's selection</p>
            <p className="font-manrope text-[#C17C3A] font-semibold text-lg">All desserts £5.95</p>
          </div>
        </div>

        <p className="text-center mt-10 font-manrope text-[#4A4A4A] text-sm italic">
          * Menu items and prices are subject to change. Please ask staff for allergen information.
        </p>
      </div>
    </section>
  );
};

// Drinks Section
const DrinksSection = () => {
  const [drinks, setDrinks] = useState({
    lagers: [], ciders: [], ales: [], bottledBeers: [], bottledCiders: [], wines: [], spirits: [], softDrinks: []
  });
  const [loading, setLoading] = useState(true);

  // Default drinks (used if database is empty)
  const defaultDrinks = {
    lagers: [
      { name: "Birra Moretti Pint", price: "£5.60" },
      { name: "Cruz Pint", price: "£5.10" },
      { name: "Fosters", price: "£4.30" },
    ],
    ciders: [
      { name: "Strongbow", price: "£4.60" },
      { name: "Inches", price: "£4.70" },
    ],
    ales: [
      { name: "John Smiths Pint", price: "£4.20" },
      { name: "Real Ale Pint", price: "£4.90" },
      { name: "Guinness Pint", price: "£6.00" },
    ],
    bottledBeers: [
      { name: "Bottled Beers", price: "£4.95" },
      { name: "Alcohol Free", price: "£4.50" },
    ],
    bottledCiders: [
      { name: "Old Mout", price: "£4.95" },
    ],
    wines: [
      { name: "House White Wine 175ml", price: "£4.95" },
      { name: "House White Wine 250ml", price: "£6.65" },
      { name: "House Red Wine 175ml", price: "£4.95" },
      { name: "House Red Wine 250ml", price: "£6.65" },
      { name: "House Rosé 175ml", price: "£4.95" },
      { name: "House Rosé 250ml", price: "£6.65" },
      { name: "Small Bottle Prosecco", price: "£7.30" },
      { name: "House Wine White (Bottle)", price: "£19.95" },
    ],
    spirits: [
      { name: "Smirnoff Vodka", price: "£3.10" },
      { name: "Gordons Gin", price: "£3.10" },
      { name: "Bells Whiskey", price: "£3.10" },
      { name: "Captain Morgan's Rum", price: "£2.50" },
    ],
    softDrinks: [
      { name: "Ask at bar for selection", price: "" },
    ],
  };

  useEffect(() => {
    const fetchDrinks = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drinks`);
        const data = await res.json();
        if (data.items && data.items.length > 0) {
          const grouped = {};
          Object.keys(defaultDrinks).forEach(cat => grouped[cat] = []);
          data.items.forEach(item => {
            if (grouped[item.category]) {
              grouped[item.category].push(item);
            }
          });
          setDrinks(grouped);
        } else {
          setDrinks(defaultDrinks);
        }
      } catch (err) {
        setDrinks(defaultDrinks);
      } finally {
        setLoading(false);
      }
    };
    fetchDrinks();
  }, []);

  if (loading) {
    return (
      <section id="drinks" className="py-20 px-6 bg-[#2C1810]">
        <div className="flex justify-center items-center py-12">
          <div className="w-8 h-8 border-4 border-[#C17C3A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="drinks" data-testid="drinks-section" className="py-20 px-6 bg-[#2C1810]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Quench your thirst</p>
          <h2 className="font-playfair text-[#F5F1E6] text-4xl md:text-5xl font-semibold">Drinks</h2>
          <p className="font-manrope text-[#F5F1E6]/70 mt-4 text-sm">Prices shown as Pint / Half Pint where applicable</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Lagers */}
          {drinks.lagers.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <Beer className="text-[#C17C3A]" size={24} />
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Lagers</h3>
              </div>
              <div className="space-y-4">
                {drinks.lagers.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-lager-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ciders */}
          {drinks.ciders.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <Beer className="text-[#C17C3A]" size={24} />
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Ciders</h3>
              </div>
              <div className="space-y-4">
                {drinks.ciders.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-cider-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ales & Stouts */}
          {drinks.ales.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <Beer className="text-[#C17C3A]" size={24} />
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Ales & Stouts</h3>
              </div>
              <div className="space-y-4">
                {drinks.ales.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-ale-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottled Beers */}
          {drinks.bottledBeers && drinks.bottledBeers.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[#C17C3A] text-2xl">🍺</span>
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Bottled Beers</h3>
              </div>
              <div className="space-y-4">
                {drinks.bottledBeers.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-bottled-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Bottled Ciders */}
          {drinks.bottledCiders && drinks.bottledCiders.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[#C17C3A] text-2xl">🍎</span>
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Bottled Ciders</h3>
              </div>
              <div className="space-y-4">
                {drinks.bottledCiders.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-bottledcider-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wines */}
          {drinks.wines.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[#C17C3A] text-2xl">🍷</span>
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Wines</h3>
              </div>
              <div className="space-y-4">
                {drinks.wines.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-wine-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A]">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Spirits */}
          {drinks.spirits.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[#C17C3A] text-2xl">🥃</span>
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Spirits</h3>
              </div>
              <div className="space-y-4">
                {drinks.spirits.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-spirit-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A]">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Soft Drinks */}
          {drinks.softDrinks && drinks.softDrinks.length > 0 && (
            <div className="bg-[#F5F1E6]/10 backdrop-blur-sm p-6 rounded-lg border border-[#F5F1E6]/20">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[#C17C3A] text-2xl">🥤</span>
                <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Soft Drinks</h3>
              </div>
              <div className="space-y-4">
                {drinks.softDrinks.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between" data-testid={`drink-soft-${index}`}>
                    <span className="font-manrope text-[#F5F1E6]/90 text-sm">{item.name}</span>
                    <span className="font-manrope text-[#C17C3A] text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="text-center mt-10 font-manrope text-[#F5F1E6]/60 text-sm">
          Bottled beers, alcopops and other drinks also available. Please drink responsibly.
        </p>
      </div>
    </section>
  );
};

// Room Hire Section
const RoomHireSection = () => {
  return (
    <section id="room-hire" data-testid="room-hire-section" className="py-20 px-6 bg-[#F5F1E6]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Host your event with us</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold">Room Hire</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* The Conservatory */}
          <div className="bg-white p-8 rounded-lg shadow-lg border border-[#E5E0D6] hover:shadow-xl transition-shadow duration-300">
            <h3 className="font-playfair text-[#2C1810] text-2xl font-semibold mb-4">The Conservatory</h3>
            <p className="font-manrope text-[#4A4A4A] mb-6">
              Our spacious conservatory is perfect for larger gatherings, wedding receptions, 
              christenings, and celebration parties. Accommodates up to 40 guests.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Buffet catering options
              </li>
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Free parking on-site
              </li>
            </ul>
            <p className="font-playfair text-[#C17C3A] text-lg font-semibold">Contact for Quotes and Details</p>
          </div>

          {/* The Stables */}
          <div className="bg-white p-8 rounded-lg shadow-lg border border-[#E5E0D6] hover:shadow-xl transition-shadow duration-300">
            <h3 className="font-playfair text-[#2C1810] text-2xl font-semibold mb-4">The Stables</h3>
            <p className="font-manrope text-[#4A4A4A] mb-6">
              Sheltered outdoor seating separate from the main building, perfect for small parties 
              of under 50 guests. Features its own gas heater, speaker system, dart board and lighting.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Own speaker system
              </li>
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Gas heater for comfort
              </li>
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Dart board available
              </li>
              <li className="flex items-center gap-2 font-manrope text-[#4A4A4A]">
                <span className="text-[#C17C3A]">✓</span> Dedicated lighting
              </li>
            </ul>
            <p className="font-playfair text-[#C17C3A] text-lg font-semibold">Contact for Quotes and Details</p>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="font-manrope text-[#4A4A4A]">
            Contact us to discuss your requirements and receive a personalised quote.
          </p>
        </div>
      </div>
    </section>
  );
};

// Live Sports Section - Football Fixtures
const LiveSportsSection = () => {
  const [footballData, setFootballData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live-sports`);
        const data = await response.json();
        setFootballData(data.sports?.football || null);
        setLastUpdated(data.last_updated);
      } catch (error) {
        console.error("Error fetching sports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSports();
    // Refresh every 30 minutes (data is cached daily on backend)
    const interval = setInterval(fetchSports, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) return "Today";
    if (date.getTime() === tomorrow.getTime()) return "Tomorrow";
    
    const options = { weekday: 'long', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "TBC";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const eventsByDate = footballData?.events_by_date || {};
  const sortedDates = Object.keys(eventsByDate).sort();

  return (
    <section id="live-sports" data-testid="live-sports-section" className="py-20 px-6 bg-[#2C1810]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Catch the action</p>
          <h2 className="font-playfair text-[#F5F1E6] text-4xl md:text-5xl font-semibold">Live Football</h2>
          <p className="font-manrope text-[#F5F1E6]/70 mt-4">Watch live football on our big screens - next 7 days</p>
          {footballData && (
            <p className="font-manrope text-[#C17C3A] mt-2">
              ⚽ {footballData.total_events} {footballData.total_events === 1 ? 'match' : 'matches'} this week
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-4 border-[#C17C3A] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : sortedDates.length > 0 ? (
          <div className="space-y-8">
            {sortedDates.map((date) => (
              <div key={date}>
                {/* Date Header */}
                <div className="flex items-center gap-3 mb-4">
                  <Calendar size={20} className="text-[#C17C3A]" />
                  <h3 className="font-playfair text-[#C17C3A] text-xl font-semibold">
                    {formatDate(date)}
                  </h3>
                  <div className="flex-1 h-px bg-[#C17C3A]/30"></div>
                </div>
                
                {/* Events Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {eventsByDate[date].map((event, index) => (
                    <div 
                      key={event.id || index}
                      className="bg-[#F5F1E6]/10 backdrop-blur-sm rounded-lg p-4 border border-[#F5F1E6]/20 hover:border-[#C17C3A]/50 transition-colors"
                    >
                      {/* League */}
                      <p className="font-manrope text-[#C17C3A] text-xs mb-3 truncate">
                        {event.league}
                      </p>
                      
                      {/* Teams */}
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex-1 text-center">
                          {event.home_badge && (
                            <img src={event.home_badge} alt="" className="w-10 h-10 mx-auto mb-1 object-contain" />
                          )}
                          <p className="font-manrope text-[#F5F1E6] text-sm font-medium truncate">
                            {event.home_team}
                          </p>
                        </div>
                        <span className="font-playfair text-[#C17C3A] text-lg font-bold">vs</span>
                        <div className="flex-1 text-center">
                          {event.away_badge && (
                            <img src={event.away_badge} alt="" className="w-10 h-10 mx-auto mb-1 object-contain" />
                          )}
                          <p className="font-manrope text-[#F5F1E6] text-sm font-medium truncate">
                            {event.away_team}
                          </p>
                        </div>
                      </div>
                      
                      {/* Time */}
                      <div className="flex items-center justify-center gap-1 text-[#F5F1E6]/70 bg-[#2C1810] rounded py-1">
                        <Clock size={14} />
                        <span className="font-manrope text-sm font-medium">{formatTime(event.time)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#F5F1E6]/5 rounded-lg">
            <p className="font-manrope text-[#F5F1E6]/50">No football matches scheduled this week</p>
          </div>
        )}

        {lastUpdated && (
          <p className="text-center mt-8 font-manrope text-[#F5F1E6]/40 text-xs">
            Schedule updated: {new Date(lastUpdated).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} • Refreshes daily
          </p>
        )}

        <div className="mt-12 text-center">
          <div className="inline-block bg-[#C17C3A]/20 border border-[#C17C3A]/40 rounded-lg px-6 py-4">
            <p className="font-manrope text-[#F5F1E6] text-sm">
              <span className="font-semibold">Big match coming up?</span> Give us a call to reserve a table with a view of the screen!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// What's On Section - Now fetches from database
const WhatsOnSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const defaultEvents = [
    { day: "Monday", title: "No Food Served", time: "", description: "Kitchen closed - drinks only" },
    { day: "Thursday", title: "Pub Quiz Night", time: "9:00 PM", description: "Test your knowledge and win prizes! Teams welcome" },
    { day: "Sunday", title: "Sunday Lunch", time: "12:00 - 2:00 PM", description: "Traditional roast with all the trimmings. Booking recommended" },
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`);
        const data = await res.json();
        if (data.items && data.items.length > 0) {
          setEvents(data.items);
        } else {
          setEvents(defaultEvents);
        }
      } catch (err) {
        setEvents(defaultEvents);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const getEventsForDay = (day) => events.filter(e => e.day === day);

  return (
    <section id="whats-on" data-testid="whats-on-section" className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">Plan your visit</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold">What's On</h2>
        </div>

        <div className="grid md:grid-cols-7 gap-4">
          {days.map((day) => {
            const dayEvents = getEventsForDay(day);
            return (
              <div 
                key={day}
                data-testid={`whats-on-${day.toLowerCase()}`}
                className={`rounded-lg p-4 min-h-[200px] ${
                  dayEvents.length > 0 
                    ? 'bg-[#F5F1E6] border-2 border-[#C17C3A]' 
                    : 'bg-[#F5F1E6]/50 border border-[#E5E0D6]'
                }`}
              >
                <h3 className={`font-playfair text-lg font-semibold mb-3 pb-2 border-b ${
                  dayEvents.length > 0 ? 'text-[#C17C3A] border-[#C17C3A]/30' : 'text-[#2C1810] border-[#E5E0D6]'
                }`}>
                  {day}
                </h3>
                
                {dayEvents.length > 0 ? (
                  <div className="space-y-3">
                    {dayEvents.map((event, eventIndex) => (
                      <div key={event.id || eventIndex}>
                        <p className="font-manrope text-[#2C1810] font-semibold text-sm">{event.title}</p>
                        {event.time && (
                          <p className="font-manrope text-[#C17C3A] text-xs font-medium">{event.time}</p>
                        )}
                        <p className="font-manrope text-[#4A4A4A] text-xs mt-1">{event.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-manrope text-[#4A4A4A]/50 text-sm italic">Check back soon</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  const events = [
    {
      title: "Quiz Night",
      day: "Every Thursday",
      time: "8:00 PM",
      description: "Test your knowledge and win prizes! Teams of up to 6.",
    },
    {
      title: "Sunday Roast Special",
      day: "Every Sunday",
      time: "12:00 - 5:00 PM",
      description: "Traditional roast with all the trimmings. Booking recommended.",
    },
    {
      title: "Live Music",
      day: "Last Saturday of Month",
      time: "8:00 PM",
      description: "Local bands and artists performing live.",
    },
    {
      title: "Curry Club",
      day: "Wednesday Evenings",
      time: "5:00 - 9:00 PM",
      description: "Homemade curry with a pint for just £12.95.",
    },
  ];

  return (
    <section id="events" data-testid="events-section" className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">What's happening</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold">Upcoming Events</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {events.map((event, index) => (
            <div
              key={event.id || `event-${index}`}
              data-testid={`event-card-${index}`}
              className="flex gap-6 bg-[#F5F1E6] p-6 rounded-lg border border-[#E5E0D6] hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex-shrink-0 w-16 h-16 bg-[#C17C3A] rounded-lg flex items-center justify-center">
                <Calendar className="text-white" size={28} />
              </div>
              <div>
                <h3 className="font-playfair text-[#2C1810] text-xl font-semibold mb-1">{event.title}</h3>
                <p className="font-manrope text-[#C17C3A] font-medium text-sm mb-2">
                  {event.day} • {event.time}
                </p>
                <p className="font-manrope text-[#4A4A4A]">{event.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewCard = ({ review, index }) => {
  return (
    <div
      data-testid={`review-card-${index}`}
      className="bg-white p-8 rounded-lg shadow-lg border border-[#E5E0D6] hover:shadow-xl transition-shadow duration-300"
    >
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={20} className="fill-[#C17C3A] text-[#C17C3A]" />
        ))}
      </div>
      <p className="font-manrope text-[#4A4A4A] leading-relaxed italic">"{review.text}"</p>
    </div>
  );
};

const ReviewsSection = () => {
  const reviews = [
    {
      text: "Love this pub food is superb open log fire and great drinks. Family friendly pub definitely recommended",
    },
    {
      text: "Lovely pub, home cooked food, plenty of staff and very clean. Landlord very friendly and engaging with customers. Menu prices are very reasonable, can't be faulted. I would say you need to book, even on a tuesday evening it was busy!",
    },
    {
      text: "Came here for a family special birthday. Easy to book, table set out lovely and a lovely warm welcome when we arrived. Food was amazing as always. Proper pub food!",
    },
  ];

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 px-6 bg-[#F5F1E6]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-caveat text-[#C17C3A] text-2xl mb-2">What our guests say</p>
          <h2 className="font-playfair text-[#2C1810] text-4xl md:text-5xl font-semibold mb-6">Customer Reviews</h2>
          
          {/* Rating Summary */}
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="https://www.tripadvisor.co.uk/Restaurant_Review-g1471645-d6952102-Reviews-or45-The_Three_Horseshoes_Inn-Blackwood_Caerphilly_County_South_Wales_Wales.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-tripadvisor-link"
              className="flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className={i < 4 ? "fill-[#C17C3A] text-[#C17C3A]" : "fill-[#C17C3A]/40 text-[#C17C3A]/40"} />
                ))}
              </div>
              <span className="font-manrope text-[#2C1810] font-medium">4.4 on Tripadvisor</span>
            </a>
            <a 
              href="https://www.google.com/maps/place/The+Three+Horseshoes+Inn/data=!4m7!3m6!1s0x486e21cb3a93802b:0xf1f1d94ca49998b6!8m2!3d51.658722!4d-3.164292!16s%2Fg%2F11ckqrp9lg!19sChIJK4CTOsshbkgRtpiZpEzZ8fE?authuser=0&hl=en&rclk=1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-maps-link"
              className="flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className={i < 5 ? "fill-[#C17C3A] text-[#C17C3A]" : "fill-[#C17C3A]/40 text-[#C17C3A]/40"} />
                ))}
              </div>
              <span className="font-manrope text-[#2C1810] font-medium">4.6 on Google Maps</span>
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {reviews.map((review, index) => (
            <ReviewCard key={review.name || `review-${index}`} review={review} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact/Footer Section - Now fetches from database
const ContactSection = () => {
  const [contactInfo, setContactInfo] = useState({
    phone: "",
    email: "",
    monday_thursday_hours: "",
    friday_saturday_hours: "",
    sunday_hours: "",
  });

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-info`);
        const data = await res.json();
        setContactInfo(data);
      } catch (err) {
        console.error("Error fetching contact info:", err);
      }
    };
    fetchContact();
  }, []);

  return (
    <footer id="contact" data-testid="contact-section" className="py-16 px-6 bg-[#2C1810]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-[#F5F1E6] text-4xl md:text-5xl font-semibold mb-4">Get In Touch</h2>
          <p className="font-manrope text-[#F5F1E6]/70">We'd love to hear from you</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Address - Links to maps */}
          <div className="text-center">
            <div className="w-14 h-14 bg-[#C17C3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="text-[#C17C3A]" size={28} />
            </div>
            <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold mb-2">Address</h3>
            <a 
              href="https://maps.google.com/?q=The+Three+Horseshoes,+High+St,+Pentwyn-mawr,+Newbridge,+Newport+NP11+4HN"
              target="_blank"
              rel="noopener noreferrer"
              className="font-manrope text-[#F5F1E6]/80 hover:text-[#C17C3A] transition-colors cursor-pointer block"
            >
              The Three Horseshoes<br />
              High St, Pentwyn-mawr<br />
              Newbridge, Newport<br />
              NP11 4HN
            </a>
            <span className="text-sm text-[#F5F1E6]/60 mt-1 block">Tap to open in maps</span>
          </div>

          {/* Phone - Click to call */}
          <div className="text-center">
            <div className="w-14 h-14 bg-[#C17C3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="text-[#C17C3A]" size={28} />
            </div>
            <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold mb-2">Phone</h3>
            {contactInfo.phone ? (
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="font-manrope text-[#F5F1E6]/80 hover:text-[#C17C3A] transition-colors block"
              >
                {contactInfo.phone}
              </a>
            ) : (
              <p className="font-manrope text-[#F5F1E6]/80">[Phone number placeholder]</p>
            )}
            <span className="text-sm text-[#F5F1E6]/60">Tap to call</span>
          </div>

          {/* Email - Click to email */}
          <div className="text-center">
            <div className="w-14 h-14 bg-[#C17C3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="text-[#C17C3A]" size={28} />
            </div>
            <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold mb-2">Email</h3>
            {contactInfo.email ? (
              <a 
                href={`mailto:${contactInfo.email}`}
                className="font-manrope text-[#F5F1E6]/80 hover:text-[#C17C3A] transition-colors block"
              >
                {contactInfo.email}
              </a>
            ) : (
              <p className="font-manrope text-[#F5F1E6]/80">[Email placeholder]</p>
            )}
            <span className="text-sm text-[#F5F1E6]/60">Tap for enquiries</span>
          </div>
        </div>

        {/* Opening Hours */}
        <div className="bg-[#F5F1E6]/10 backdrop-blur-sm rounded-lg p-8 mb-12 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Clock className="text-[#C17C3A]" size={24} />
            <h3 className="font-playfair text-[#F5F1E6] text-xl font-semibold">Opening Hours</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="font-manrope text-[#F5F1E6] font-medium">Monday - Thursday</p>
              <p className="font-manrope text-[#F5F1E6]/70">{contactInfo.monday_thursday_hours || "[Hours placeholder]"}</p>
            </div>
            <div>
              <p className="font-manrope text-[#F5F1E6] font-medium">Friday - Saturday</p>
              <p className="font-manrope text-[#F5F1E6]/70">{contactInfo.friday_saturday_hours || "[Hours placeholder]"}</p>
            </div>
            <div className="col-span-2">
              <p className="font-manrope text-[#F5F1E6] font-medium">Sunday</p>
              <p className="font-manrope text-[#F5F1E6]/70">{contactInfo.sunday_hours || "[Hours placeholder]"}</p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center pt-8 border-t border-[#F5F1E6]/20">
          <p className="font-manrope text-[#F5F1E6]/50 text-sm">
            © {new Date().getFullYear()} The Three Horseshoes. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Website Credits */}
      <div className="bg-[#1a0f0a] py-6 mt-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <a 
            href="https://builtbyjac.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block group"
          >
            <div className="text-[#F5F1E6]/70 tracking-[0.3em] text-sm font-light uppercase hover:text-[#F5F1E6] transition-colors">
              BUILT BY JAC
            </div>
          </a>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            <a 
              href="mailto:builtbyjac@gmail.com"
              className="text-[#F5F1E6]/60 hover:text-[#C17C3A] transition-colors"
            >
              builtbyjac@gmail.com
            </a>
            <span className="text-[#F5F1E6]/30">•</span>
            <a 
              href="https://instagram.com/built.by.jac" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#F5F1E6]/60 hover:text-[#C17C3A] transition-colors"
            >
              @built.by.jac
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      data-testid="scroll-to-top-btn"
      onClick={scrollToTop}
      className={`fixed bottom-20 right-8 z-[9999] w-12 h-12 bg-[#C17C3A] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#A0652D] transition-all duration-300 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
      }`}
    >
      <ChevronUp size={24} />
    </button>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <Navigation />
      <HeroSection />
      <UpdatesSection />
      <AboutSection />
      <GallerySection />
      <MenuSection />
      <DrinksSection />
      <LiveSportsSection />
      <WhatsOnSection />
      <RoomHireSection />
      <ReviewsSection />
      <ContactSection />
      <ScrollToTop />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="app-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/preorder" element={<PreorderTemplate />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
